import type { ShorthandValue, TileImageProps } from '@archipro-design/aria';

import type { DetailedPinWithChecked } from '../component/all-pins/AllPins';
import { TileTopAction } from '../component/shared/TileTopAction';
import { DesignBoardPinTargetType } from 'generated/graphql';

import type { ArchizeenTileData } from '~/modules/design-board/type';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';

export const getPinTile = ({
    pin,
    isChecked,
    onUpdate,
    onDelete,
    isAllPins,
}: {
    pin: DetailedPinWithChecked;
    isChecked: boolean;
    onUpdate: (id: number, checked: boolean) => void;
    onDelete: (id: number) => void;
    isAllPins?: boolean;
}): React.ReactElement => {
    const deletePin = (id: number) => {
        onDelete(id);
    };

    const isArticleTile = isPinTileType<ArchizeenTileData>(
        pin.TileData,
        DesignBoardPinTargetType.Article
    );

    const isArticle = pin.TargetFilterType === DesignBoardPinTargetType.Article;

    return (
        <div className="group relative">
            <div className="absolute left-0 top-0 z-10 w-full p-2 transition-opacity duration-200 md:p-0 lg:opacity-0 lg:group-hover:opacity-100 [&>div]:w-full">
                <TileTopAction
                    //Implent something for hiding share if type is a file/image
                    isChecked={isChecked}
                    id={pin.ID}
                    onUpdate={onUpdate}
                    deletePin={(id) => deletePin(id)}
                    isAllPins={isAllPins}
                    isArticleTile={isArticleTile}
                    professionalLink={getPinWebsiteLink(pin)}
                    pin={pin}
                />
            </div>
            <Tile
                tile={{
                    aspectRatio: isArticle ? 'article' : 'default',
                    model: {
                        ...pin.TileData,
                        ...(isArticle && {
                            variant: 'featured',
                        }),
                        badge: null,
                    },
                    hideFollowButton: true,
                }}
                key={pin.TileData.id}
            />
        </div>
    );
};

export const isPinTileType = <
    T extends {
        pinType: DesignBoardPinTargetType;
    },
>(
    tile: Record<string, unknown>,
    type: DesignBoardPinTargetType
): tile is T => {
    return 'pinType' in tile && (tile as T).pinType === type;
};

export const getUploadFileTileImage = (
    src: string,
    alt = '',
    objectFit = 'cover'
) => {
    return {
        images: [
            {
                src,
                alt,
                objectFit,
                height: '100%',
                width: '100%',
            },
        ] as TileImageProps['images'],
    } as ShorthandValue<TileImageProps>;
};

export const getPinWebsiteLink = (
    pin: Pick<DetailedPinWithChecked, 'Target'>
) => {
    switch (pin.Target.__typename) {
        case 'Project':
        case 'Product':
        case 'Article':
            return pin.Target.Professional?.Website || '';
        case 'Professional':
            return pin.Target.Website || '';
        case 'MemberUpload':
        case 'LibraryImage':
        default:
            return '';
    }
};

export const getPinProfessional = (
    pin: Pick<DetailedPinWithChecked, 'Target'>
) => {
    switch (pin.Target.__typename) {
        case 'Project':
        case 'Product':
        case 'Article':
            return pin.Target.Professional;
        case 'Professional':
            return pin.Target;
        case 'MemberUpload':
        case 'LibraryImage':
        default:
            return undefined;
    }
};
